import { Box, Button, Flex, Heading, Input, InputGroup, Text } from '@chakra-ui/react';
import { UseFormReturn, useController } from 'react-hook-form';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { SelectField } from 'src/components/SelectField';
import { DateRangeFilterFields } from '@/components/DateRangeFilterFields';
import { DateRangeChangeHandler } from '@/components/DateRangeFilterFields/types';
import { PostsFilters } from '../../types';
import { Club, PostStatus } from '@/model';
import { useTranslation } from 'react-i18next';
import TeamFilterSelect from '@/components/TeamFilterSelect';

export interface PostsFiltersBoxProps {
  filtersForm: UseFormReturn<PostsFilters>;
  forceRefetch: () => void;
  clubs: Club[];
}

export const PostsFiltersBox = ({ clubs, filtersForm }: PostsFiltersBoxProps) => {
  const { t } = useTranslation();
  const { register, reset, control } = filtersForm;

  const dateValueControl = useController({
    name: 'dateValue',
    control,
  });
  const startDateControl = useController({
    name: 'start_date',
    control,
  });
  const endDateControl = useController({
    name: 'end_date',
    control,
  });

  const handleRangeChange: DateRangeChangeHandler = (startDate, endDate) => {
    startDateControl.field.onChange(startDate);
    endDateControl.field.onChange(endDate);
  };

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>{t('pages.application_posts.components.posts_filters_box.posts_filters_title')}</Heading>
      </Flex>

      <Flex direction={{ base: 'column' }} gap='small' rowGap='micro'>
        <Flex direction={{ base: 'column', md: 'row' }} alignItems={{ md: 'flex-end' }} wrap='wrap' gap={6}>
          <TeamFilterSelect showFlag name='team_uuid' control={filtersForm.control} clubs={clubs} width='20%' />

          <InputGroup w={{ base: '100%', md: 'fit-content' }} display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_posts.components.posts_filters_box.posts_filters_content_label')}
            </Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('content'))} />
          </InputGroup>

          <InputGroup w={{ base: '100%', md: '130px' }} display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_posts.components.posts_filters_box.posts_filters_status_label')}
            </Text>
            <SelectField {...register('status')}>
              <option value=''>
                {t('pages.application_posts.components.posts_filters_box.posts_filters_status_option_all')}
              </option>
              <option value={PostStatus.PUBLISHED}>
                {t('pages.application_posts.components.posts_filters_box.posts_filters_status_option_published')}
              </option>
              <option value={PostStatus.NOT_PUBLISHED}>
                {t('pages.application_posts.components.posts_filters_box.posts_filters_status_option_not_published')}
              </option>
              <option value={PostStatus.DRAFT}>
                {t('pages.application_posts.components.posts_filters_box.posts_filters_status_option_draft')}
              </option>
            </SelectField>
          </InputGroup>

          <DateRangeFilterFields
            onRangeChange={handleRangeChange}
            startDate={startDateControl.field.value}
            endDate={endDateControl.field.value}
            dateValue={dateValueControl.field.value}
            onDateValueChange={dateValueControl.field.onChange}
            inputName='interactive-area-posts-date'
            startDateRootProps={{ w: { base: '100%', md: 'fit-content' } }}
            endDateRootProps={{ w: { base: '100%', md: 'fit-content' } }}
          />
        </Flex>

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={() => reset()}>
          {t('pages.application_posts.components.posts_filters_box.posts_filters_button_clear')}
        </Button>
      </Flex>
    </Box>
  );
};
